import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { useState, useRef, useCallback, useMemo } from 'react';
import { styled, Divider } from '@mui/material';
import MuiBox from '@mui/material/Box';
import { usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import Menu from '@mui/material/Menu';
import '../../../OverflowWindow/OverflowWindow.js';
import OverflowProvider from '../../../OverflowWindow/OverflowProvider.js';
import '../../../OverflowWindow/components/MenuCategory.js';
import '../../../OverflowWindow/components/ChecklistItems.js';
import MenuItem from '../../../OverflowWindow/components/MenuItem.js';
import '../../../OverflowWindow/components/SubMenuItem.js';
import 'tslib';
import '../../../OverflowWindow/components/CascadingProvider.js';
import { ReactComponent as ForwardRef } from '../../../../assets/icons/ElipsisSmall.svg.js';

const Root = styled(MuiBox) `
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledEllipsisIcon = styled(ForwardRef, {
    shouldForwardProp: prop => !['panel', 'isOpen'].includes(prop),
}) `
  ${({ theme, panel, isOpen }) => `
    cursor: pointer;
    opacity: ${panel ? '1' : '0'};

    ${isOpen &&
    `
        opacity: 1;
        > path {
          fill: ${panel ? 'white' : theme.palette.primary.light};
        }
      `}

    &:hover {
      > path {
        fill: ${panel ? 'white' : theme.palette.primary.light};
      }
    }
    &:focus {
      > path {
        fill: ${panel ? 'white' : theme.palette.primary.light};
      }
    }

    > path {
      fill: ${panel ? 'white' : theme.palette.N30.main};
    }
  `}
`;
const MenuTriggerWrapper = styled(MuiBox) `
  width: 30px;
  height: 38px;
  border-top: solid 4px transparent;
  border-bottom: solid 4px transparent;
`;
const MenuTriggerContainer = styled(MuiBox, {
    shouldForwardProp: prop => !['panel', 'isOpen'].includes(prop),
}) `
  ${({ theme, panel, isOpen }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    border-radius: 4px;

    &:hover {
      background-color: ${panel ? null : 'rgba(42, 65, 90)'};
      cursor: pointer;
      svg {
          > path {
              fill: ${panel ? 'white' : theme.palette.primary.main};
          }
      }
    }
    
    ${isOpen &&
    `
        background-color: ${panel ? null : 'rgba(42, 65, 90)'};
        cursor: pointer;
        svg {
          > path {
            fill: ${panel ? 'white' : theme.palette.primary.main};
          }
        }
      `}
  `}
`;
const StyledMenu = styled(Menu, {
    shouldForwardProp: prop => !['rtl'].includes(prop),
}) `
  ${({ rtl }) => `
    ${rtl
    ? `
        margin-left: 5px;
        direction: rtl;

        li {
          padding: 0 8px;
        }
      `
    : `
        margin: 5px 0 0 0;
      `};
  `}
`;
const RowMenu = React__default.memo(({ row, denseRowMenu = true, panel, getRowMenu, onRowMenuOpen, onRowMenuClose, rtl }) => {
    const popupState = usePopupState({ variant: 'popover', popupId: 'row-menu' });
    const [openMenu, setOpenMenu] = useState(false);
    const anchorEl = useRef(null);
    const onMenuOpen = useCallback((e) => {
        e.stopPropagation();
        setOpenMenu(true);
        if (row) {
            onRowMenuOpen === null || onRowMenuOpen === void 0 ? void 0 : onRowMenuOpen(row);
        }
    }, [onRowMenuOpen, row]);
    const onMenuClose = useCallback(() => {
        setOpenMenu(false);
        if (row) {
            onRowMenuClose === null || onRowMenuClose === void 0 ? void 0 : onRowMenuClose(row);
        }
    }, [onRowMenuClose, row]);
    const onMenuItemClick = useCallback((children, onClick, e) => {
        e.stopPropagation();
        onClick(row, children, e);
        onMenuClose();
    }, [onMenuClose, row]);
    const menuProps = useMemo(() => {
        const props = Object.assign(Object.assign({}, bindPopover(popupState)), { onClick: (e) => e.stopPropagation(), disableAutoFocusItem: true, sx: { p: 1 } });
        return rtl
            ? Object.assign(Object.assign({}, props), { transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                } }) : props;
    }, [popupState, rtl]);
    const menuItems = useMemo(() => {
        if (!getRowMenu || !row) {
            return [];
        }
        return getRowMenu(row) || [];
    }, [getRowMenu, row]);
    const hasItems = useMemo(() => {
        let itemsSum = 0;
        menuItems.map(({ items }) => (itemsSum += items.length));
        return itemsSum;
    }, [menuItems]);
    if (!hasItems) {
        return null;
    }
    if (!menuItems.length || !getRowMenu) {
        return null;
    }
    return (jsx(Root, Object.assign({ onContextMenu: e => {
            e.preventDefault();
            e.stopPropagation();
            onMenuClose();
        } }, { children: jsxs(OverflowProvider, Object.assign({ dense: denseRowMenu }, { children: [jsx(MenuTriggerWrapper, Object.assign({ "data-testid": "row-menu-button", onClick: onMenuOpen }, { children: jsx(MenuTriggerContainer, Object.assign({ panel: panel, isOpen: openMenu, ref: anchorEl }, { children: jsx(StyledEllipsisIcon, { className: "ops-row-action-menu", panel: panel, isOpen: openMenu }) })) })), jsx(StyledMenu, Object.assign({ rtl: rtl, style: { marginLeft: rtl ? '50px' : '-50px', marginTop: '5px' } }, menuProps, { open: openMenu, "data-testid": "row-menu", onClose: onMenuClose, disableAutoFocusItem: true, anchorReference: "anchorEl", anchorEl: anchorEl.current, transformOrigin: {
                        vertical: 'top',
                        horizontal: rtl ? 'right' : 'left',
                    } }, { children: menuItems.map(({ items }, index) => {
                        return [
                            ...items.map(({ children, onClick, disabled, hint }, index) => (jsx(MenuItem, Object.assign({ "data-testid": `menuItem-${index}`, onClick: e => onMenuItemClick(children, onClick, e), disabled: disabled, hint: hint }, { children: children }), `menuItem-${index}`))),
                            ...(index + 1 < menuItems.length && menuItems[index + 1].items.length
                                ? [jsx(Divider, {}, `${index}-divider`)]
                                : []),
                        ];
                    }) }))] })) })));
});
RowMenu.displayName = 'RowMenu';

export { RowMenu as default };
//# sourceMappingURL=RowMenu.js.map
